import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-file-unknow-line text-foreground-400 text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-600 mb-6">抱歉，您访问的文章不存在或已下架</p>
          <Link to="/" className="whitespace-nowrap inline-block px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: {
            '@type': 'Organization',
            name: '女仆教育',
          },
          publisher: {
            '@type': 'Organization',
            name: '女仆教育',
          },
        }}
      />
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://maid-edu.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: category?.name || '课程分类',
              item: category ? `https://maid-edu.com/category/${category.slug}` : 'https://maid-edu.com/category/liyi-guifan',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: article.title,
            },
          ],
        }}
      />
      <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: category?.name || '课程分类', to: category ? `/category/${category.slug}` : '/category/liyi-guifan' },
            { label: article.title },
          ]}
        />

        <header className="mb-8">
          <div className="flex items-center flex-wrap gap-3 mb-4">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {category.name}
              </Link>
            )}
            <span className="text-xs text-foreground-500">{article.date}</span>
            <span className="text-xs text-foreground-500 flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime} 阅读
            </span>
          </div>

          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
            {article.title}
          </h1>

          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
            {article.excerpt}
          </p>
        </header>

        <div className="mb-10 rounded-xl overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-64 md:h-80 lg:h-96 object-cover object-top"
          />
        </div>

        <div
          className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-4"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="mt-12 pt-8 border-t border-background-200">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <span>分享文章：</span>
              <a href="#" className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" rel="nofollow">
                <i className="ri-wechat-line"></i>
              </a>
              <a href="#" className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" rel="nofollow">
                <i className="ri-weibo-line"></i>
              </a>
              <a href="#" className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer" rel="nofollow">
                <i className="ri-link"></i>
              </a>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-100 transition-all cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3 md:p-4">
                      <h4 className="font-heading text-xs md:text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                      <div className="mt-2 flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-time-line"></i>
                        <span>{ra.readTime} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-5 md:p-6 rounded-xl bg-background-100">
          <div>
            <h3 className="font-heading font-semibold text-foreground-900 mb-1">对女仆培训感兴趣？</h3>
            <p className="text-sm text-foreground-600">了解更多课程信息，开启您的职业之旅</p>
          </div>
          <Link
            to="/about"
            className="whitespace-nowrap px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            立即咨询
          </Link>
        </div>
      </article>
    </div>
  );
}